import { Channel } from "@redotech/cockroachdb-codegen/congodb/channel";
import {
  SchemaInstance,
  defineSchema,
} from "@redotech/redo-flows-core-schema-common/type-system/schema";
import { MOCK_PRODUCT_IMAGE_URL } from "@redotech/redo-marketing-mock-common/mock-values";
import { Provider } from "@redotech/redo-order-common/provider";
import { CompensationMethod } from "@redotech/redo-return-core-common/compensation-method";
import { ReturnTypeEnum } from "@redotech/redo-return-core-common/return-type-enum";
import {
  baseTrackingSchema,
  exampleBaseTrackingSchemaInstance,
} from "./base-tracking";
import { exampleOrderTrackingSchemaInstance } from "./order-tracking";

export const returnTrackingSchema = defineSchema({
  fields: {
    ...baseTrackingSchema.fields,
    returnPackingInstructions: {
      dataType: "Maybe Multiple Text",
      documentation: "Instructions for returning the items",
      hideFromConditionStep: true,
    },
    emailAttachments: {
      dataType: "Multiple Email Attachment",
      documentation: "The attached items in a return confirmation email",
      hideFromConditionStep: true,
    },
    compensationMethod: {
      dataType: "Multiple Enum",
      documentation: "The method of compensation for the return",
      values: Object.values(CompensationMethod),
    },
    customerRequestType: {
      dataType: "Enum",
      documentation: "The type of the return",
      values: Object.values(ReturnTypeEnum),
    },
    notes: {
      dataType: "Object",
      documentation:
        "Notes from the merchant to the customer (Use `.` to access nested fields)",
      hideFromConditionStep: true,
      hideFromInsertionModal: true,
    },
    notesToCustomerOnApproval: {
      dataType: "Maybe Text",
      documentation: "The note from the merchant to the customer on approval",
      hideFromConditionStep: true,
    },
    notesToCustomerOnProcessing: {
      dataType: "Maybe Text",
      documentation: "The note from the merchant to the customer on processing",
      hideFromConditionStep: true,
    },
    returnRejectedReason: {
      dataType: "Maybe Text",
      documentation: "The reason the return was rejected",
      hideFromConditionStep: true,
      hideFromInsertionModal: true,
    },
    merchantRejectionNotes: {
      dataType: "Maybe Text",
      documentation:
        "Notes from the merchant explaining why they rejected the return",
      hideFromConditionStep: true,
    },
    merchantActionMessage: {
      dataType: "Maybe Text",
      documentation:
        "Optional message authored by the merchant when approving, rejecting, or processing the return (rendered in the Order Summary block of the matching email)",
      hideFromConditionStep: true,
      hideFromInsertionModal: true,
    },
    merchantActionImageUrls: {
      dataType: "Multiple Text",
      documentation:
        "Image URLs attached by the merchant when approving, rejecting, or processing the return (rendered in the Order Summary block of the matching email, and attached to the email)",
      hideFromConditionStep: true,
      hideFromInsertionModal: true,
    },
    returnFlowRejectionReasons: {
      dataType: "Maybe Text",
      documentation:
        "The rejection reasons from the return flow (e.g. outside return window, final sale)",
      hideFromConditionStep: true,
    },
    packagePickupDayOfWeek: {
      dataType: "Maybe Text",
      documentation: "The day of the week the package will be picked up",
      hideFromConditionStep: true,
    },
    packagePickupDetail: {
      dataType: "Maybe Text",
      documentation: "Details for picking up the package",
      hideFromConditionStep: true,
    },
    returnDetailsLink: {
      dataType: "Url",
      documentation: "The link to view the return details",
      hideFromConditionStep: true,
    },
    completeExchangeLink: {
      dataType: "Maybe Url",
      documentation: "The link to complete an exchange",
      hideFromConditionStep: true,
    },
    pickupSurveyLink: {
      dataType: "Maybe Url",
      documentation: "The link to a survey about the pickup experience",
      hideFromConditionStep: true,
    },
    pickupRescheduleLink: {
      dataType: "Maybe Url",
      documentation: "The link to reschedule a pickup",
      hideFromConditionStep: true,
    },
    pickupCancelLink: {
      dataType: "Maybe Url",
      documentation: "The link to cancel a pickup",
      hideFromConditionStep: true,
    },
    returnDetailsQrCode: {
      dataType: "Maybe Text",
      hideFromConditionStep: true,
      documentation: "The QR code for customers to show to store associates",
    },
    storeLocationsLink: {
      dataType: "Url",
      documentation: "The link to the store locations",
      hideFromConditionStep: true,
    },
    isBlueYonderReturn: {
      dataType: "Boolean",
      hideFromInsertionModal: true,
      hideFromConditionStep: true,
      documentation: "Whether the return is a blue yonder return or not",
    },
    returnPortalLoginLink: {
      dataType: "Url",
      documentation: "The link to the return portal",
      hideFromConditionStep: true,
    },
    storeName: {
      dataType: "Text",
      documentation: "The name of your store",
      hideFromConditionStep: true,
    },
    storeEmail: {
      dataType: "Text",
      documentation: "The default email of your store",
      hideFromConditionStep: true,
    },
    hasGreenReturnItems: {
      dataType: "Boolean",
      hideFromInsertionModal: true,
      documentation:
        "Whether the return contains any green return items (items the customer can keep)",
    },
    allItemsAreGreenReturn: {
      dataType: "Boolean",
      hideFromInsertionModal: true,
      documentation:
        "Whether all items in the return are green return items (customer keeps all items)",
    },
    exchangeOutOfStockPolicy: {
      dataType: "Text",
      documentation:
        'The pretty-formatted policy for out-of-stock exchanges. Resolves from the per-product return strategies on the return — one of "sent you a refund", "sent you store credit", or "sent you a refund and store credit" when the OOS items resolve to a mix of both.',
      hideFromConditionStep: true,
    },
    isRepair: {
      dataType: "Boolean",
      hideFromInsertionModal: true,
      documentation: "Whether the return is for repair (true) or not (false)",
    },
    returnSubmittedDate: {
      dataType: "Text",
      hideFromConditionStep: true,
      documentation: "The date when the return was submitted by the customer",
    },
    returnExpirationDate: {
      dataType: "Maybe Text",
      hideFromConditionStep: true,
      documentation: "The date when the return expires and is no longer valid",
    },
    isAnInstantExchange: {
      dataType: "Boolean",
      hideFromInsertionModal: true,
      documentation: "Whether the return was an instant exchange or not",
    },
    isAnInStoreReturn: {
      dataType: "Boolean",
      hideFromInsertionModal: true,
      documentation: "Whether the return is an in-store return or not",
    },
    isDropoffReturn: {
      dataType: "Boolean",
      hideFromInsertionModal: false,
      hideFromConditionStep: false,
      documentation:
        "Whether the customer chose a dropoff location for this return",
    },
    channel: {
      dataType: "Maybe Text",
      documentation: "The channel the return was created from",
      hideFromConditionStep: true,
    },
    returnTotalValue: {
      dataType: "Text",
      documentation:
        "The total value of the return including taxes and adjustments",
      hideFromConditionStep: true,
    },
    isManualLabel: {
      dataType: "Boolean",
      hideFromInsertionModal: true,
      documentation: "Whether the return was manually created or not",
    },
    boxlessReturnsQrCode: {
      dataType: "Multiple Url To Qr Code",
      documentation:
        "The URL to the QR code for customers to show at boxless drop off locations",
      hideFromConditionStep: true,
    },
    boxlessReturnsLocations: {
      dataType: "Maybe Url",
      documentation: "The link to view boxless drop off locations",
      hideFromConditionStep: true,
    },
    returnTags: {
      dataType: "Multiple Text",
      documentation: "Tags associated with the return",
    },
    returnLabelUrls: {
      dataType: "Multiple Url",
      documentation: "URLs to download return shipping labels",
      hideFromConditionStep: true,
    },
    commercialInvoiceUrls: {
      dataType: "Multiple Url",
      documentation:
        "URLs to download commercial invoices for international shipments",
      hideFromConditionStep: true,
    },
    provider: {
      dataType: "Enum",
      documentation:
        "The ecommerce provider for this return (e.g., shopify, commentsold, bigcommerce)",
      values: Object.values(Provider),
    },
    sendCustomerNotificationsForProcessing: {
      dataType: "Boolean",
      documentation:
        "Whether customer notifications are enabled for this return when it is processed. Based on merchant settings and can be toggled during manual processing.",
    },
    isManuallyProcessingReturn: {
      dataType: "Boolean",
      documentation:
        "Whether the latest processing trigger for this return was manual.",
      hideFromInsertionModal: true,
    },
    storeCreditAmount: {
      dataType: "Maybe Text",
      documentation: "The formatted store credit amount (e.g. $25.00)",
      hideFromConditionStep: true,
    },
    storeCreditActionUrl: {
      dataType: "Maybe Url",
      documentation:
        "The URL to activate or use the store credit (discount link or store URL)",
      hideFromConditionStep: true,
    },
    storeCreditDiscountCode: {
      dataType: "Maybe Text",
      documentation:
        "The discount code for the store credit, if applicable (not set for account credits)",
      hideFromConditionStep: true,
    },
    storeCreditExpirationDate: {
      dataType: "Maybe Text",
      documentation: "The expiration date of the store credit, if applicable",
      hideFromConditionStep: true,
    },
    storeCreditType: {
      dataType: "Maybe Text",
      documentation:
        "The type of store credit issued (e.g. discount_code, shopify_account, gift_card)",
      hideFromConditionStep: true,
    },
  },
});

export const exampleReturnTrackingSchemaInstance: SchemaInstance<
  typeof returnTrackingSchema
> = {
  ...exampleBaseTrackingSchemaInstance,
  customerRequestType: ReturnTypeEnum.RETURN,
  // order in name for backwards compatibility
  orderSummary: {
    ...exampleBaseTrackingSchemaInstance.orderSummary,
    trackableType: {
      lowerCase: "return",
      titleCase: "Return",
      verb: "returned",
    },
    rejectedLineItems: [
      {
        title: "Product 2",
        variantTitle: "Variant 2",
        image: { src: MOCK_PRODUCT_IMAGE_URL },
        price: 10.0,
        isRedo: false,
        requiresShipping: true,
        tags: [],
        properties: {},
        sku: "SKU-2",
        quantity: 1,
        id: 2,
        green_return: false,
        priceSet: { presentment_money: { amount: 10.0, currency_code: "USD" } },
      },
    ],
  },
  isBlueYonderReturn: false,
  tracker: {
    ...exampleOrderTrackingSchemaInstance.tracker,
    trackableType: {
      lowerCase: "return",
      titleCase: "Return",
      verb: "returned",
    },
  },
  returnPackingInstructions: [
    "Print out the return label",
    "Pack the items in a box",
    "Drop off the box at the nearest post office",
  ],
  emailAttachments: [],
  notes: { notesToCustomer: "Test note from merchant to customer" },
  notesToCustomerOnApproval: "Test note from merchant to customer on approval",
  notesToCustomerOnProcessing:
    "Test note from merchant to customer on processing",
  returnRejectedReason:
    "The return was rejected because the items were damaged",
  merchantRejectionNotes: "Items showed signs of wear beyond normal use",
  merchantActionMessage: undefined,
  merchantActionImageUrls: [],
  returnFlowRejectionReasons:
    "This item is outside the return policy window. If you have any questions, please contact customer service.",
  packagePickupDayOfWeek: "Monday",
  packagePickupDetail: "at the front door",
  returnDetailsLink: "https://example.com/return-details",
  completeExchangeLink: "https://example.com/complete-exchange",
  pickupSurveyLink: "https://example.com/pickup-survey",
  pickupRescheduleLink: "https://example.com/pickup-reschedule",
  pickupCancelLink: "https://example.com/pickup-cancel",
  compensationMethod: ["refund", "exchange"],
  returnDetailsQrCode: "https://assets.getredo.com/qr-code-example.png",
  storeLocationsLink: "https://example.com/locations",
  returnPortalLoginLink: "https://example.com/return-portal",
  storeEmail: "example@email.com",
  storeName: "Example Store",
  hasGreenReturnItems: false,
  allItemsAreGreenReturn: false,
  exchangeOutOfStockPolicy: "sent you a refund",
  isRepair: false,
  returnSubmittedDate: "2024-01-15",
  returnExpirationDate: "2024-02-15",
  isAnInstantExchange: false,
  isAnInStoreReturn: false,
  isDropoffReturn: false,
  channel: Channel.Web,
  returnTotalValue: "$100.00",
  isManualLabel: false,
  boxlessReturnsQrCode: [{ url: "https://example.com/boxless-qr-code" }],
  boxlessReturnsLocations: "https://example.com/boxless-locations",
  returnTags: ["high-value", "vip-customer"],
  returnLabelUrls: [
    "https://example.com/return-label-1.pdf",
    "https://example.com/return-label-2.pdf",
  ],
  commercialInvoiceUrls: ["https://example.com/commercial-invoice-1.pdf"],
  provider: Provider.SHOPIFY,
  sendCustomerNotificationsForProcessing: true,
  isManuallyProcessingReturn: true,
  storeCreditAmount: "$25.00",
  storeCreditActionUrl: "https://example.com/discount/ABC123",
  storeCreditDiscountCode: "ABC123",
  storeCreditExpirationDate: "2024-03-15",
  storeCreditType: "discount_code",
};
